import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";

const SelectedExercisesItemTemplate = ({ exercise, handleDelete, isEditModeActive, setExerciseVideoDialogVisible, setIsCollapsedDetails, setSelectedExercise, setSelectedExerciseForVideo, t, moveCard, index }: any) => {
    const handleClick = () => {
        if (isEditModeActive) return;
        setSelectedExerciseForVideo(exercise?.exercise);
        setExerciseVideoDialogVisible(true);
    }

    const handleDeleteOrEdit = (e: any) => {
        e.stopPropagation();
        if (!isEditModeActive) {
            handleDelete(exercise);
        } else {
            setSelectedExercise(exercise);
            setIsCollapsedDetails(true);
        }
    }

    const ref = useRef<HTMLDivElement>(null);
        
    const [{ handlerId }, drop] = useDrop({
        accept: "exercise",
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item: any, monitor: any) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            
            if (!clientOffset) {
                return;
            }
            
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            moveCard(dragIndex, hoverIndex);

            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag({
        type: "exercise",
        canDrag: () => !isEditModeActive,
        item: () => {
            return { id: exercise.exercise.id, index };
        },
        collect: (monitor: any) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const opacity = isDragging ? 0.4 : 1;
    drag(drop(ref));

    return (
        <div ref={ref} className={`col-12 flex relative ${!isEditModeActive ? "cursor-pointer" : ""}`} onClick={handleClick} style={{ opacity }}>
            {!isEditModeActive && <img src="/dragIcon.svg" style={{ cursor: "grab", width: '2.5rem' }} />}
            <div className="flex flex-wrap w-full align-items-center justify-content-between bg-white py-1 px-3 h-auto min-h-7rem" style={{ borderRadius: "18px" }}>
                <Button
                    onClick={handleDeleteOrEdit}
                    className="absolute w-2rem h-2rem"
                    style={{ backgroundColor: isEditModeActive ? "#2D3967" : "#E3506B", border: "none", top: "5px", right: "2px" }}
                    icon={isEditModeActive ? "pi pi-pencil" : "pi pi-times"}
                    rounded
                    aria-label={isEditModeActive ? "Edit" : "Delete"}
                />
                <img
                    src={exercise?.exercise?.image}
                    alt={exercise?.exercise?.name}
                    className="flex-shrink-0"
                    style={{
                        width: "4rem",
                        height: "4rem",
                        objectFit: "contain",
                        borderRadius: "12px",
                        marginRight: "12px"
                    }}
                />
                <div className="flex-grow-1 flex-shrink-1 mr-2" style={{ maxWidth: '30%' }}>
                    <p className="text-lg font-bold m-0"
                        style={{ color: "#2D3967" }}>
                        {exercise?.exercise?.name}
                    </p>
                </div>
                <div className="flex flex-column gap-2 flex-grow-0 flex-shrink-1">
                    <div className="flex gap-2">
                        <span className="text-400 font-light text-md">{!exercise?.isEveryOtherDay ? t("days") : t("alternateDays")}</span>
                        {!exercise?.isEveryOtherDay ? (
                            <>
                                <Tooltip target=".selected-exercise-days-tooltip-span" />
                                <span className="selected-exercise-days-tooltip-span font-bold text-overflow-ellipsis overflow-hidden white-space-nowrap"
                                    style={{ color: "#2D3967", maxWidth: "100px" }}
                                    data-pr-tooltip={exercise?.days?.map((day: any) => t(day)).join(", ")} data-pr-position="bottom">
                                    {exercise?.days?.map((day: any) => t(day)).join(", ")}
                                </span>
                            </>
                        ) : (
                            <span className="font-bold text-overflow-ellipsis overflow-hidden white-space-nowrap"
                                style={{ color: "#2D3967", maxWidth: "100px" }}>
                                {exercise?.everyOtherDayPeriod}
                            </span>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <span className="text-400 font-light whitespace-nowrap text-md">{t("tolerance")}</span>
                        <span className="font-bold text-overflow-ellipsis overflow-hidden"
                            style={{ color: "#2D3967" }}>
                            {exercise?.tolerance + "%"}
                        </span>
                    </div>
                </div>
                <div className="flex flex-column gap-2 flex-grow-0 flex-shrink-1">
                    <div className="flex gap-2">
                        <span className="text-400 font-light whitespace-nowrap text-md">{t("set")}, {t("reps")}</span>
                        <span className="font-bold text-overflow-ellipsis overflow-hidden"
                            style={{ color: "#2D3967" }}>
                            {exercise?.set + " x " + exercise?.repeat}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <span className="text-400 font-light whitespace-nowrap text-md">{t("breakTime")}</span>
                        <span className="text-overflow-ellipsis overflow-hidden"
                            style={{ color: "#2D3967" }}>
                            {exercise?.restInterval + " " + t("hours")}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SelectedExercisesItemTemplate;